import React, {useEffect, useState} from "react";

// Ark Nova card assets are all 281x391 px.
const CARD_WIDTH = 281;
const CARD_HEIGHT = 391;
export const CARD_ASPECT_RATIO = CARD_WIDTH / CARD_HEIGHT;

// Geometry of the cost token as burnt into the cards by the python generator.
// Animals carry a money token pasted at (7, 50) sized 42x42, with the cost drawn
// centered on (27, 71) at font size 25. Sponsors carry a level token pasted at
// (4, 4), sized 53x42 -- its 130px wide source scaled by 47/115 -- with the
// level drawn centered on (29, 26) at font size 28.
const ANIMAL_COST = {
    image: "money_empty",
    icon: {x: 7, y: 50, width: 42, height: 42},
    text: {centerX: 27, centerY: 71, fontSize: 25},
    reducedField: "reducedAnimalCost",
    increasedField: "increasedAnimalCost"
};
const SPONSOR_COST = {
    image: "level_sponsor_empty",
    icon: {x: 4, y: 4, width: 53, height: 42},
    text: {centerX: 29, centerY: 26, fontSize: 28},
    reducedField: "sponsorReducedCost"
};

// Geometry of the requirement badges, same source: badge i is pasted at
// (4, baseY + (1 + i) * 42) with a size of 36x36, over a background made of a
// rectangle (0, y - 2, 22, y + 38) plus a circle of radius 20 centered on
// (22, y + 18) -- together a 42x40 pill rounded on its right end only.
// baseY is where the two generators differ: 62 for animals, 30 for sponsors.
const REQ_BADGE = {x: 4, step: 36 + 6, size: 36};
const REQ_PILL = {x: 0, offsetY: -2, width: 42, height: 40};
// A reputation requirement carries its threshold, drawn white over the badge.
const REQ_TEXT = {fontSize: 25};

// What each card type gets: both show requirements and a cost, but sponsors lay
// their badges out higher up the card and their cost is a level token, only ever
// reduced, where an animal cost can go either way.
const CARD_TYPES = {
    ANIMAL: {requirementBaseY: 62, cost: ANIMAL_COST},
    SPONSOR: {requirementBaseY: 30, cost: SPONSOR_COST}
};

// The overlays are positioned in percentages of the card, so their edges land on
// subpixels once the card is scaled down and never quite line up with the shapes
// rasterized into the image. Each repainted shape is grown outwards by this many
// card pixels so that it always covers the one it hides, whichever way the
// browser rounds. Growing the pill by BLEED on its right and by BLEED top and
// bottom keeps it concentric: its radius becomes 20 + BLEED around (22, y + 18).
// Keep it at 1: at 2 the pills of consecutive requirements start overlapping.
const OVERLAY_BLEED = 1;

const COST_COLOR_REDUCED = "rgb(46, 204, 113)";
const COST_COLOR_INCREASED = "rgb(231, 76, 60)";
// pastel green, to sit at the same intensity as the salmon red the generator
// uses for badges and which is kept for unmet requirements.
const REQ_COLOR_MET = "rgb(143, 209, 145)";
const REQ_COLOR_UNMET = "rgb(221, 87, 73)";

// Geometry of the grey panel the generator draws in the top-left corner of the
// animal cards, behind the enclosure icons and the cost token. It is the union
// of a banner (-10, -10, w, 49), whose width w depends on the enclosures the
// animal accepts, and of a column (-10, -10, 58, 98) under it. Rather than
// rebuilding that layout, the panel is found back by flood filling its exact
// grey from (1, 1): the banner is repainted after hasSuitableEnclosure and the
// column after canAfford. The fill never leaves region, nor the column once
// below the banner, so that greyish pixels of the photo touching the panel
// stay as they are.
const PANEL = {
    grey: 135,
    tolerance: 2,
    seed: {x: 1, y: 1},
    region: {width: 230, height: 110},
    bannerBottom: 49,
    columnRight: 58
};
// same colours as the requirement pills, as RGB components
const PANEL_RGB_OK = [143, 209, 145];
const PANEL_RGB_KO = [221, 87, 73];

// Every requirement badge shipped under /public/arknova, as named by the card
// generator. Together with the reputation one below they cover every badge
// printed on the animal and sponsor cards, each checked against the pixels of
// the cards themselves. A requirement whose name is not in here keeps the badge
// printed on the card untouched, so a new badge only needs its PNG dropped in
// and a line added here.
const REQUIREMENT_IMAGES = [
    "continent_africa", "continent_americas", "continent_asia", "continent_australia", "continent_europe",
    "type_bear", "type_bird", "type_herbivore", "type_marine", "type_pet", "type_predator", "type_primate",
    "type_reptile", "animals ii", "sponsors ii", "partner zoo", "science", "universities", "kiosk",
    "reputation", "max appeal 25"
];
// Reputation is the one badge the generator reuses across thresholds: the value
// is written over it rather than baked in, so "reputation 6" resolves here.
const REPUTATION_PREFIX = "reputation";
const REPUTATION_IMAGE = "reputation";

const pctW = (px) => (100 * px / CARD_WIDTH) + "%";
const pctH = (px) => (100 * px / CARD_HEIGHT) + "%";

const normalizeCardName = (name) => (name || "")
    .toLowerCase()
    .replaceAll(" ", "_")
    .replaceAll("'", "")
    .replaceAll("-", "_")
    .replaceAll(":", "");

export const cardImageSrc = (card) => {
    if (!card || !card.name || !card.cardType) return null;
    const type = card.cardType.toLowerCase();
    return "/public/arknova/cards/" + type + "s/card_" + type + "_" + normalizeCardName(card.name) + ".png";
};

const assetUrl = (imgName) => "/public/arknova/" + imgName.split("/").map(encodeURIComponent).join("/") + ".png";

// Requirements reach us in whatever casing and separator the backend uses
// (EUROPE, partner zoo, PARTNER_ZOO, reputation 6...), so names are matched on
// their letters and digits only.
const squash = (value) => (value || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");

// Indexes every badge by its squashed file name, and also by its squashed name
// stripped of the prefix the generator adds to group its images.
const REQUIREMENT_INDEX = REQUIREMENT_IMAGES.reduce((index, imgName) => {
    const fileName = imgName.split("/").pop();
    index[squash(fileName)] = imgName;
    const bare = fileName.replace(/^(continent|type|bonus|icon_requirement)_/, "");
    if (bare !== fileName) index[squash(bare)] = imgName;
    return index;
}, {});

/**
 * Resolves the badge of a requirement, plus the value to write over it for the
 * reputation ones ("reputation 6" -> "6"). Returns null for an unknown
 * requirement, in which case the badge printed on the card is left untouched.
 */
export const requirementBadge = (requirement) => {
    const key = squash(requirement);
    if (!key) return null;
    if (key.startsWith(REPUTATION_PREFIX)) {
        return {src: assetUrl(REPUTATION_IMAGE), label: key.slice(REPUTATION_PREFIX.length) || null};
    }
    const imgName = REQUIREMENT_INDEX[key];
    return imgName ? {src: assetUrl(imgName), label: null} : null;
};

/**
 * Resolves the cost overlay to display on a card, following the cost layout of
 * its type. Returns null when the type has no adjusted cost to show.
 */
const getCostOverlay = (overlay, cardType) => {
    const cost = cardType && cardType.cost;
    if (!overlay || !cost) return null;
    if (overlay[cost.reducedField] > -1) {
        return {...cost, value: overlay[cost.reducedField], color: COST_COLOR_REDUCED};
    }
    if (cost.increasedField && overlay[cost.increasedField] > -1) {
        return {...cost, value: overlay[cost.increasedField], color: COST_COLOR_INCREASED};
    }
    return null;
};

/**
 * Resolves one entry per requirement badge to repaint, in the order the
 * generator laid them out. A requirement without an explicit prerequisitesMet
 * flag is considered unmet, which keeps the colour the card already shows.
 */
const getRequirementOverlays = (overlay, cardType) => {
    if (!overlay || !overlay.requirements || !cardType) return [];
    return overlay.requirements.map((requirement, i) => {
        const badge = requirementBadge(requirement);
        if (!badge) return null;
        return {
            requirement,
            src: badge.src,
            label: badge.label,
            met: !!(overlay.prerequisitesMet && overlay.prerequisitesMet[i]),
            y: cardType.requirementBaseY + (1 + i) * REQ_BADGE.step
        };
    }).filter(Boolean);
};

/**
 * Resolves the colours of the grey panel of an animal card: its banner for the
 * enclosure, its column for the cost. Returns null when the overlay does not
 * carry both flags, in which case the panel keeps its grey.
 */
const getPanelOverlay = (overlay, card) => {
    if (!overlay || (card.cardType || "").toUpperCase() !== "ANIMAL") return null;
    if (typeof overlay.hasSuitableEnclosure !== "boolean" || typeof overlay.canAfford !== "boolean") return null;
    return {
        banner: overlay.hasSuitableEnclosure ? PANEL_RGB_OK : PANEL_RGB_KO,
        column: overlay.canAfford ? PANEL_RGB_OK : PANEL_RGB_KO
    };
};

/**
 * Repaints the grey panel of a loaded card image and returns the result as a
 * data URL. Pixels of the panel take the colour of the part they belong to;
 * the antialiased ring around it is shifted halfway towards that colour, which
 * keeps the rounded corners smooth.
 */
const repaintPanel = (img, panel) => {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    const {width, height} = PANEL.region;
    const imageData = ctx.getImageData(0, 0, width, height);
    const px = imageData.data;
    const isPanelGrey = (i) => Math.abs(px[i] - PANEL.grey) <= PANEL.tolerance
        && Math.abs(px[i + 1] - PANEL.grey) <= PANEL.tolerance
        && Math.abs(px[i + 2] - PANEL.grey) <= PANEL.tolerance;
    const colorAt = (y) => y <= PANEL.bannerBottom ? panel.banner : panel.column;

    // 0: not visited, 1: panel, 2: visited outside of the panel
    const state = new Uint8Array(width * height);
    const stack = [PANEL.seed.y * width + PANEL.seed.x];
    while (stack.length) {
        const p = stack.pop();
        if (state[p]) continue;
        const x = p % width;
        const y = (p - x) / width;
        if (!isPanelGrey(4 * p) || (y > PANEL.bannerBottom && x > PANEL.columnRight)) {
            state[p] = 2;
            continue;
        }
        state[p] = 1;
        if (x > 0) stack.push(p - 1);
        if (x < width - 1) stack.push(p + 1);
        if (y > 0) stack.push(p - width);
        if (y < height - 1) stack.push(p + width);
    }

    for (let p = 0; p < width * height; p++) {
        const x = p % width;
        const y = (p - x) / width;
        const color = colorAt(y);
        const i = 4 * p;
        if (state[p] === 1) {
            px[i] = color[0];
            px[i + 1] = color[1];
            px[i + 2] = color[2];
            continue;
        }
        const touchesPanel = (x > 0 && state[p - 1] === 1) || (x < width - 1 && state[p + 1] === 1)
            || (y > 0 && state[p - width] === 1) || (y < height - 1 && state[p + width] === 1);
        if (!touchesPanel) continue;
        const shift = ((px[i] + px[i + 1] + px[i + 2]) / 3 - PANEL.grey) / 2;
        for (let c = 0; c < 3; c++) {
            px[i + c] = Math.max(0, Math.min(255, Math.round((px[i + c] + color[c] + shift) / 2)));
        }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
};

// Repainted cards, by image and panel colours: a hand of cards is rendered on
// every state update and each repaint costs a canvas round trip.
const repaintedCards = new Map();

/**
 * Returns the source to display for a card: the image with its panel repainted
 * once that is ready, the plain image until then or when there is nothing to
 * repaint.
 */
const useRepaintedSrc = (src, panel) => {
    const key = panel ? src + "|" + panel.banner + "|" + panel.column : null;
    const [repainted, setRepainted] = useState(() => key ? repaintedCards.get(key) || null : null);

    useEffect(() => {
        if (!key) {
            setRepainted(null);
            return;
        }
        if (repaintedCards.has(key)) {
            setRepainted(repaintedCards.get(key));
            return;
        }
        setRepainted(null);
        let cancelled = false;
        const img = new Image();
        img.onload = () => {
            let url;
            try {
                url = repaintPanel(img, panel);
            } catch (e) {
                // a tainted canvas or a missing 2d context: keep the plain card
                return;
            }
            repaintedCards.set(key, url);
            if (!cancelled) setRepainted(url);
        };
        img.src = src;
        return () => {
            cancelled = true;
        };
    }, [key]);

    return repainted || src;
};

/**
 * Displays an Ark Nova card image from a card placeholder ({name, cardType}).
 * Fills the parent as much as possible while keeping the card aspect ratio.
 * When adaptInfo is true, the player cardOverlay for this card (if any) is
 * repainted on top of the image: each requirement badge in green (met) or red
 * (unmet), and the adjusted cost in green (reduced) or red (increased) -- the
 * animal cost on its money token, the sponsor level on its level token. On an
 * animal, the grey panel behind the enclosure and the cost is repainted too:
 * its banner green when an enclosure fits the animal, its column green when the
 * player can afford it, each red otherwise.
 */
export const DisplayCard = ({card, adaptInfo = false, player = null}) => {
    const src = cardImageSrc(card);
    const overlay = src && adaptInfo && player && player.cardOverlays ? player.cardOverlays[card.name] : null;
    const panelOverlay = getPanelOverlay(overlay, card);
    // called before the early return below, hooks must run on every render
    const displayedSrc = useRepaintedSrc(src, panelOverlay);
    if (!src) return null;

    const cardType = CARD_TYPES[(card.cardType || "").toUpperCase()];
    const costOverlay = getCostOverlay(overlay, cardType);
    const requirementOverlays = getRequirementOverlays(overlay, cardType);

    return (
        <div style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden"
        }}>
            <div style={{
                position: "relative",
                height: "100%",
                aspectRatio: CARD_ASPECT_RATIO,
                maxWidth: "100%",
                maxHeight: "100%",
                // makes the cq* units below resolve against the card itself
                containerType: "size"
            }}>
                <img
                    src={displayedSrc}
                    alt={"img " + card.name}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        display: "block"
                    }}/>

                {costOverlay && (
                    <React.Fragment>
                        {/* blank token, hides the cost burnt into the card image */}
                        <img
                            src={assetUrl(costOverlay.image)}
                            alt={""}
                            style={{
                                position: "absolute",
                                left: pctW(costOverlay.icon.x - OVERLAY_BLEED),
                                top: pctH(costOverlay.icon.y - OVERLAY_BLEED),
                                width: pctW(costOverlay.icon.width + 2 * OVERLAY_BLEED),
                                height: pctH(costOverlay.icon.height + 2 * OVERLAY_BLEED),
                                display: "block",
                                pointerEvents: "none"
                            }}/>
                        <div style={{
                            position: "absolute",
                            left: pctW(costOverlay.text.centerX),
                            top: pctH(costOverlay.text.centerY),
                            transform: "translate(-50%, -50%)",
                            color: costOverlay.color,
                            fontSize: (100 * costOverlay.text.fontSize / CARD_HEIGHT) + "cqh",
                            fontWeight: 700,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            pointerEvents: "none",
                            userSelect: "none"
                        }}>
                            {costOverlay.value}
                        </div>
                    </React.Fragment>
                )}

                {requirementOverlays.map((req, i) => (
                    <React.Fragment key={req.requirement + "_" + i}>
                        {/* repainted pill, hides the one burnt into the card image */}
                        <div style={{
                            position: "absolute",
                            left: pctW(REQ_PILL.x),
                            top: pctH(req.y + REQ_PILL.offsetY - OVERLAY_BLEED),
                            width: pctW(REQ_PILL.width + OVERLAY_BLEED),
                            height: pctH(REQ_PILL.height + 2 * OVERLAY_BLEED),
                            // clamped down by the browser to a 20 + BLEED circular
                            // radius, i.e. the circle the generator draws
                            borderRadius: "0 999px 999px 0",
                            backgroundColor: req.met ? REQ_COLOR_MET : REQ_COLOR_UNMET,
                            pointerEvents: "none"
                        }}/>
                        <img
                            src={req.src}
                            alt={"requirement " + req.requirement}
                            style={{
                                position: "absolute",
                                left: pctW(REQ_BADGE.x),
                                top: pctH(req.y),
                                width: pctW(REQ_BADGE.size),
                                height: pctH(REQ_BADGE.size),
                                display: "block",
                                pointerEvents: "none"
                            }}/>
                        {req.label && (
                            <div style={{
                                position: "absolute",
                                left: pctW(REQ_BADGE.x + REQ_BADGE.size / 2),
                                top: pctH(req.y + REQ_BADGE.size / 2),
                                transform: "translate(-50%, -50%)",
                                color: "white",
                                fontSize: (100 * REQ_TEXT.fontSize / CARD_HEIGHT) + "cqh",
                                fontWeight: 700,
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                userSelect: "none"
                            }}>
                                {req.label}
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

export default DisplayCard;
